import React from 'react';
import { Link } from 'react-router-dom';
import CustomVideo from 'components/shared/custom-video';
import MainHeader, {
  HeaderText,
  Underline,
} from 'components/shared/landing/main-header';
import { IconList, IconListItem } from 'components/shared/landing/icon-list';
import MoreInfo from 'components/shared/more-info';
import SectionHeader from 'components/shared/landing/section-header';
import styles from './styles.module.scss';
import learningCentersVideo from 'assets/videos/Your_child_DESERVES_a_chance_to_reach_ma.mp4';
import learningCentersPoster from 'assets/img/ulc-header-poster-2.png';
import playIcon from 'assets/img/video-play-button.svg';
import championIcon from 'assets/img/landing/landing-icon-champions.svg';
import contactIcon from 'assets/img/landing/landing-icon-contact.svg';
import communityIcon from 'assets/img/landing/landing-icon-community.svg';
import comparisonChart from './img/Unyted-Comparison-Page-3-v1.png';
import comparisonChartPdf from './img/Unyted-Comparison-Page-3-v1.pdf';
import WhoIsAUnytedKid from 'assets/videos/Who is a Unyted Kid 2.mp4';
import WhoIsAUnytedKidPoster from 'assets/img/who-is-a-unyted-kid-poster.png';
import HowItWorks1 from 'assets/videos/ULC_How_It_Works_Step_1.mp4';
import HowItWorks1Poster from 'assets/img/ulc-how-it-works-1-poster.png';
import HowItWorks2 from 'assets/videos/ULC_How_It_Works_Step_2.mp4';
import HowItWorks2Poster from 'assets/img/ulc-how-it-works-2-poster.png';
import HowItWorks3 from 'assets/videos/ULC_How_It_Works_Step_3.mp4';
import HowItWorks3Poster from 'assets/img/ulc-how-it-works-3-poster.png';
import { FaqContainer, FaqItem } from 'components/shared/faq-item';

const OnlineLearningCenters = ({ history }) => (
  <div className={styles.unytedLearningCenters}>
    <MainHeader
      header={
        <HeaderText className={styles.headerText}>
          Find elite private educators & live online group lessons that go{' '}
          <Underline>beyond basic video conferences</Underline>{' '}
        </HeaderText>
      }
      subtext={
        <span>
          Out of school marketplace of private educators who use an online
          collaborative solution to engage your child to learn & grow in ways
          you can measure
        </span>
      }
      subtextClassName={styles.headerSubtext}
      onButtonClick={() => history.push('/marketplace/all')}
      buttonText="Get Started"
      buttonClassName={styles.headerCTAButton}
      videoElement={
        <CustomVideo
          src={learningCentersVideo}
          poster={learningCentersPoster}
          icon={playIcon}
        />
      }
      videoElementContainerClassName={styles.video}
    />
    <section className={styles.comparison}>
      <div className="container">
        <div className="row">
          <div className="col-12">
            <SectionHeader
              tagline="comparison"
              valueprop="TEAM Method vs Traditional Alternatives"
            />
          </div>
        </div>
        <div className={styles.comparisonBody}>
          <div className="row">
            <div className="col-lg-7">
              <p className={styles.lead}>
                We believe you should not have to compromise.
              </p>
              <p>
                <strong>Typical academic tutoring solutions</strong> mainly
                focus on academic instruction, usually through individual
                assignments and worksheets. They often miss opportunities for
                deeper understanding.
              </p>
              <p>
                <strong>Other personal coaching solutions</strong> focus on
                developing students' personal skills &ndash; for example,
                executive functioning skills. Kids may understand these skills,
                but struggle to practice and apply them in the real world.
              </p>
              <p>
                <strong>Unyted Learning Centers</strong> combine both academic
                tutoring and personal coaching at deeper levels. Because they
                use Unyted's unique technology tools, our certified educators
                play the role of both tutor and coach at the same time. Your
                result is unmatched success: whole child development
                personalized for your child.
              </p>
            </div>
            <div className="col-lg-5">
              <img alt="Comparison Chart" src={comparisonChart} />
              <p className="mt-3">
                <a href={comparisonChartPdf} target="_blank" rel="noreferrer">
                  View Chart >>
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section id="how-it-works" className={styles.looksLike}>
      <div className="container">
        <div className="row">
          <div className="col-12">
            <SectionHeader
              tagline="your child's experience"
              valueprop="How It Works"
            />
          </div>
        </div>
        <div className={styles.step1}>
          <div
            className={`col-12 col-lg-6 order-2 order-lg-1 ${styles.textContainer}`}
          >
            <h5>STEP 1: First Session (Onboarding)</h5>
            <h2>Assessment &amp; Prep</h2>
            <p>
              Every Unyted learner must go through a 'first session'
              (onboarding). You will schedule this session for your child via
              Unyted's custom calendar tool. In the session, the learner will be
              introduced to their certified educator and the Unyted online
              platform. The educator will assess your child's academic strengths
              and limitations in a subject you've chosen. Your child will also
              be prepared to grow their{' '}
              <Link to="/sel-encyclopedia">SEL Power Skills.</Link>
            </p>
            <p>
              *Currently, due to COVID-19 concerns, we're only doing online
              learning sessions.
            </p>
          </div>
          <div className="col-12 col-lg-6 order-1 order-lg-2">
            <CustomVideo
              src={HowItWorks1}
              poster={HowItWorks1Poster}
              icon={playIcon}
            />
          </div>
        </div>
        <div className={styles.step2}>
          <div className="col-12 col-lg-6">
            <CustomVideo
              src={HowItWorks2}
              poster={HowItWorks2Poster}
              icon={playIcon}
            />
          </div>
          <div className={`col-12 col-lg-6 ${styles.textContainer}`}>
            <h5>STEP 2: Types of Learning</h5>
            <h2>Individual vs Group</h2>
            <p>
              Based on the type of plan you purchase, your child will do
              individual or group sessions. You'll have access to schedule
              additional sessions after the first session (onboarding). During
              these later sessions, your child's certified educator will provide
              instruction applying The TEAM Method. If you've chosen 1 on 1
              sessions, your child's educator will insert themselves into the
              lesson and pair with your child for group problem-solving. If
              you've chosen group sessions, your child will work with carefully
              selected peers to problem solve. These group-based scenarios are
              specifically created to develop both academic and SEL Power Skills
              at the same time.
            </p>
          </div>
        </div>
        <div className={styles.step3}>
          <div
            className={`col-12 col-lg-6 order-2 order-lg-1 ${styles.textContainer}`}
          >
            <h5>STEP 3: Personalized Data</h5>
            <h2>TEAM Method Lessons</h2>
            <p>
              The magic of Unyted is it all leads to clear feedback for you and
              your child to use in whole child development. As your child is
              guided through lessons by their certified educator, personalized
              feedback is provided and recorded on both SEL Power Skills and
              academics. This helps push your child toward mastery and becoming
              an independent learner who continuously grows!
            </p>
          </div>
          <div className="col-12 col-lg-6 order-1 order-lg-2">
            <CustomVideo
              src={HowItWorks3}
              poster={HowItWorks3Poster}
              icon={playIcon}
            />
          </div>
        </div>
        <div className={styles.startCta}>
          <div className="col-12 col-md-6 offset-md-3">
            <Link
              className="button apple w-100 text-center font-heavy"
              to="/unyted-learning-centers/all"
            >
              Find an Online Center
            </Link>
          </div>
        </div>
      </div>
    </section>
    <section className={styles.whoWeServe}>
      <div className="container">
        <div className="row">
          <div className="col-12">
            <SectionHeader
              tagline="Becoming a Unyted Kid"
              valueprop="Who We Serve Best"
            />
          </div>
        </div>
        <div className={styles.elligibleStudents}>
          <div className="row">
            <div className="col-lg-6">
              <CustomVideo
                src={WhoIsAUnytedKid}
                poster={WhoIsAUnytedKidPoster}
                icon={playIcon}
              />
            </div>
            <div className="col-lg-6">
              <p>
                <strong>
                  For all learners who are open to problem-solving:
                </strong>
              </p>
              <IconList>
                <IconListItem icon="check" iconClassName="text-apple">
                  Kinder - 12th grade
                </IconListItem>
                <IconListItem icon="check" iconClassName="text-apple">
                  All learning levels &mdash; including gifted
                </IconListItem>
                <IconListItem icon="check" iconClassName="text-apple">
                  With a mobile device or laptop
                  <MoreInfo title="Bring your own device">
                    Because instruction takes place both in-person and online,
                    each student needs a mobile device or laptop capable of
                    running a modern web browser. If you have Chrome, Firefox,
                    Safari, or Edge, you're good to go!
                  </MoreInfo>
                </IconListItem>
                <IconListItem icon="check" iconClassName="text-apple">
                  Want to learn on their own terms & interest
                </IconListItem>
                <IconListItem icon="check" iconClassName="text-apple">
                  Ready to improve their{' '}
                  <Link to="/sel-encyclopedia">SEL Power Skills</Link>
                </IconListItem>
                <IconListItem icon="check" iconClassName="text-apple">
                  Excited to grow at their own pace
                </IconListItem>
              </IconList>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section className={styles.howItWorks}>
      <div className="container">
        <div className="row">
          <div className="col-12">
            <SectionHeader tagline="easy 1-2-3" valueprop="How To Start" />
          </div>
        </div>
        <div className="row">
          <div className="col-lg-4 col-12 mb-5 mb-lg-0">
            <img
              alt="Find Learning Center"
              className={styles.icon}
              src={championIcon}
            />
            <p>
              Use search and filters to find a convenient Unyted Learning Center
              and read about what makes the certified educator a match for you.
            </p>
          </div>
          <div className="col-lg-4 col-12 mb-5 mb-lg-0">
            <img
              alt="Read More about learning center"
              className={styles.icon}
              src={communityIcon}
            />
            <p>
              Identify and purchase a learning plan or assessment for your
              child's needs and schedule your child's first session with their
              certified educator.
            </p>
          </div>
          <div className="col-lg-4 col-12 mb-5 mb-lg-0">
            <img
              alt="Connect to learning center"
              className={styles.icon}
              src={contactIcon}
            />
            <p>
              We'll email you login instructions. Once logged in, you can
              schedule sessions, contact your child's certified educator, and
              more!
            </p>
          </div>
        </div>
        <div className={styles.startCta}>
          <div className="col-12 col-md-6 offset-md-3">
            <Link
              className="button apple w-100 text-center font-heavy"
              to="/unyted-learning-centers/all"
            >
              Get Started
            </Link>
          </div>
        </div>
      </div>
    </section>

    <section className={styles.faqs}>
      <div className="container">
        <div className="row">
          <div className="col-12">
            <h2>Frequently Asked Questions</h2>
          </div>
          <div className="col-12">
            <FaqContainer>
              <FaqItem question="What is the END GOAL of becoming a 'Unyted kid?'">
                <p>
                  Each child should make progress toward becoming an
                  independent, life-long learner and someone who understands
                  self-improvement as a way to best be prepared for real world
                  success. Not many tutor/educators are explicitly trying to
                  'put themselves out of business.' Those on Unyted are because,
                  ultimately, they're even financially incentivized for your
                  child to be an independent learner within their Learning
                  Center. It changes the entire dynamic of Education and
                  Development. Everyone is aligned on what's best for your
                  child.
                </p>
              </FaqItem>
              <FaqItem question="Why is this so different from traditional academic tutoring and coaching?">
                <p>
                  Firstly, certified educators using Unyted's technology
                  seamlessly combine the two. But, even comparing traditional
                  alternatives individually, they each mostly focus on skills in
                  isolation. Except we all use these skills together. Unyted
                  recognized the science behind how academic performance
                  (knowledge) and personal skills development work together. So,
                  we created a unique group-problem solving technology that
                  helps learners best develop both types of skills at the same
                  time. More than knowing, they practice. More than listening,
                  they do. And, we created the technology tools around this
                  process so traditional tutors can transform into more
                  effective coaches.
                </p>
              </FaqItem>

              <FaqItem question="How do you back up that learners will understand faster and better?">
                <p>
                  There are evidence-based strategies proven to help your child
                  understand academic material faster. Part of the inconsistency
                  in traditional results is because we need to get the right
                  tech tools in the hands of the right educators. Unyted has
                  done that for you and your child. See research on{' '}
                  <a href="https://www.researchgate.net/publication/222704753_Active_learning_and_cooperative_learning_Understanding_the_difference_and_using_both_styles_effectively">
                    Active Learning
                  </a>
                  ,{' '}
                  <a href="https://ro.ecu.edu.au/cgi/viewcontent.cgi?article=2902&context=ajte">
                    Effective Cooperative Learning
                  </a>
                  ,{' '}
                  <a href="https://www.sciencedirect.com/science/article/pii/S2452301116300062">
                    Problem-Based Learning
                  </a>
                  , digital{' '}
                  <a href="http://www.nea.org/tools/16963.htm">
                    Project-Based Learning
                  </a>
                  , and{' '}
                  <a href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5839644/">
                    Interest Based Learning
                  </a>
                  .
                </p>
              </FaqItem>
              <FaqItem question="Can my child start with 1-on-1 sessions instead of a small group program?">
                <p>
                  Yes, though it depends on what each elite educator offers!
                  Most offer 1 on 1 sessions. In this case, you'll have to
                  schedule the first session based on the educator's
                  availability calendar.
                </p>
              </FaqItem>
              <FaqItem question="Is a 'first session'(onboarding) provided for every child?">
                <p>
                  Yes. We pride ourselves on how much we personalize your
                  child's development. To get the most of what we aim to do,
                  learners *new to Unyted* are given a personal onboarding. This
                  is either scheduled by you as part of 1-on-1 package, or the
                  onboarding is part of a small group program offered by the
                  educator. In either case, it best prepares them to get the
                  most out of future sessions and moving toward being empowered
                  as an independent learner!
                </p>
              </FaqItem>

              <FaqItem
                question={
                  'How do the Unyted Educators develop Social Emotional Skills (SEL) Power Skills in learners?'
                }
              >
                <p>
                  Whenever a team tries to solve a problem together,{' '}
                  <Link to="/sel-encyclopedia">SEL Power Skills</Link> are a
                  necessary part of success. So if your child is working in a 1
                  on 1 setting with an educator, the educator will do group
                  problem solving with your child and coach during the process.
                  If you've selected group sessions for your child, your child
                  will work with peers and the educator will coach. This unique
                  collaborative style gives Unyted educators better
                  opportunities to hone in on skill development during academic
                  instruction. It also gives learners real life opportunities
                  for your child to actually practice these key skills.
                </p>
              </FaqItem>

              <FaqItem question="How does this skill development transfer outside of academic situations and problems?">
                <p>
                  That's part of our mission! When learners are consistently put
                  in these scenarios, sure, they get better academic and SEL
                  Power Skills development. But, part of the magic is that
                  certified educators explicitly coach learners to form habits
                  that *transfer* outside of the technology. An experienced
                  Unyted learner uses{' '}
                  <Link to="/sel-encyclopedia">The TEAM Method&trade;</Link> as
                  an instinct in and out of school. At home, on the playground,
                  in sports, and in future work opportunities.
                </p>
              </FaqItem>
            </FaqContainer>
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default OnlineLearningCenters;
